import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Section, showAlert } from '@platform/ui';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import type { BoardType } from '@/shared/constants/app/routes.constant.ts';
import { useCreatePost } from '../api/use-board.mutation.ts';
import { useBoardPermissions } from '../hooks/useBoardPermissions.ts';
import { BoardForm } from '../components/BoardForm.tsx';
import { BoardStateView } from '../components/BoardStateView.tsx';
import type { BoardConfig, CreatePostInput, Post } from '../types/board.type.ts';

interface BoardWriteViewProps {
  type: BoardType;
  config: BoardConfig<Post>;
}

/**
 * 게시글 작성 컨테이너.
 * 권한(canCreate) 없으면 forbidden 뷰. 있으면 폼 → 생성 mutation → 상세로 이동.
 *
 * ⚠️ FE 가드는 UX 용. 서버가 x-user-role 로 최종 인가하므로,
 *    권한 없는 사용자가 폼을 강제로 봐도 POST 는 서버에서 거부된다.
 */
export function BoardWriteView({ type, config }: BoardWriteViewProps) {
  const navigate = useNavigate();
  const perms = useBoardPermissions(type);
  const createMutation = useCreatePost(type);

  const handleSubmit = useCallback(
    async (input: CreatePostInput) => {
      const created = await createMutation.mutateAsync(input);
      navigate(config.detailPath(created.id), { replace: true });
    },
    [createMutation, navigate, config],
  );

  const handleError = useCallback((message: string) => {
    void showAlert({ title: '알림', message, confirmText: '확인' });
  }, []);

  if (!perms.canCreate) {
    return (
      <BoardStateView
        kind="forbidden"
        message="글쓰기 권한이 없습니다."
        backTo={config.basePath}
      />
    );
  }

  const seo = buildSeo({
    title: `${config.title} 작성`,
    description: config.description,
    path: config.writePath(),
  });

  return (
    <>
      <SEO {...seo} />
      <main className="bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <Section spacing="default">
          <Container>
            <div className="mx-auto max-w-3xl pt-[100px] sm:pt-28">
              <header className="mb-8 border-b border-black/10 dark:border-white/10 pb-5">
                <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-neutral-950/40 dark:text-white/40">
                  {config.type.toUpperCase()}
                </span>
                <h1 className="mt-2 text-3xl font-extrabold -tracking-[0.03em]">
                  새 {config.title} 작성
                </h1>
              </header>
              <BoardForm
                type={type}
                config={config}
                onSubmit={handleSubmit}
                submitting={createMutation.isPending}
                onCancel={() => navigate(config.basePath)}
                onError={handleError}
              />
            </div>
          </Container>
        </Section>
      </main>
    </>
  );
}
